import React, { useEffect, useState } from "react";
import { Row, Col, Card, CardBody, Container, CardHeader } from "reactstrap";
import useUrlState from "@ahooksjs/use-url-state";
import { CustomLink } from "src/components/Common/CustomLink";
import { useTranslation } from "react-i18next";

import { getSimulators, patchSimulator } from "src/services/simulator-flight";
import { handleScrollTo, handleScrollToTop } from "src/utils/common";
import { PROCESS_STATUS, PAGINATION_DEFAULT, REFETCH_INTERVAL_TIME, RESOURCE_TYPE_FOR_TAG } from "src/constant/common";
import APP_ROUTES from "src/routes/routes";
import { useMutation, useQuery } from "src/Hooks/useReactQuery";
import { useRecoilState, useSetRecoilState } from "recoil";
import { alertState, resetFormPromptState } from "src/recoil/atoms/global";
import ModalFlightSimulatorSeriesFilesView from "src/components/Common/Modal/ModalFlightSimulatorSeriesFilesView";
import ProtectedComponent from "src/components/Permission";
import TableCommon from "src/components/Common/Table";
import useUserList from "src/Hooks/useUserList";
import useVersion from "src/Hooks/useVersion";
import useExcutionFile from "src/Hooks/useExcutionFile";
import useSeriesDetailHandler from "src/Hooks/useSeriesDetailHandler";
import { columns } from "./columns";
import ModalAssignTags from "src/components/Common/Modal/ModalAssignTags";
import useGetAllTags from "src/Hooks/TagFunction/useGetTags";
import SearchInput from "src/components/Common/SearchInput";

const FlightSimulatorList = () => {
  const { t } = useTranslation(["flight-simulator", "common"]);

  const showAlert = useSetRecoilState(alertState);
  const [resetForm, setResetForm] = useRecoilState(resetFormPromptState);
  const { data: userOptions } = useUserList(true);
  const [params, setParams] = useUrlState(
    {},
    {
      navigateMode: "replace",
      stringifyOptions: {
        skipNull: true,
        skipEmptyString: true,
      },
    }
  );
  const { data: versionData, options: versionOptions } = useVersion();
  const { data: excutionFileOptions } = useExcutionFile(versionData);
  const { fetchSeriesDetail } = useSeriesDetailHandler(versionOptions);
  const [textSearch, setTextSearch] = useState(params?.condition || null);
  const [seriesById, setSeriesById] = useState();
  const [isLoadingSimulator, setIsLoadingSimulator] = useState(true);
  const [simulationIdsOpen, setSimulationIdsOpen] = useState([]);
  const [simulationIsUpdated, setSimulationIsUpdated] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState({});
  const [allowScrollToId, setAllowScrollToId] = useState(true);
  const [dataModalFileView, setDataModalFileView] = useState({
    isOpen: false,
    serieData: {},
    fileData: {},
    simulationType: "",
    executionFileName: "",
  });
  const [singleResourceSelected, setSingleResourceSelected] = useState("");
  const [isAssignTagModalOpen, setIsAssignTagModalOpen] = useState(false);

  const { data: simulations, refetch } = useQuery(
    ["getSimulators"],
    () =>
      getSimulators({
        page: params.page || 1,
        limit: PAGINATION_DEFAULT.pageSize,
        q: params?.condition,
        ...(params.type && { type: params.type }),
        ...(params.user_ids && { user_ids: params.user_ids }),
        ...(params.version_name && { version_name: params.version_name }),
        ...(params.execution_file_name && { execution_file_name: params.execution_file_name }),
        ...(params.tag_ids && { tag_ids: params.tag_ids }),
      }),
    {
      onSuccess: (data) => {
        if (params?.id && allowScrollToId) {
          handleScrollTo(0, 90, `row${params.id}`);
          setAllowScrollToId(false);
        }
        for (const simulationItem of data.items) {
          if (
            simulationIdsOpen.some((id) => id === simulationItem.id) &&
            simulationItem.status === PROCESS_STATUS.FAILED &&
            !simulationIsUpdated.some((id) => id === simulationItem.id)
          ) {
            getSeriesDetail(simulationItem);
            setSimulationIsUpdated((oldVal) => [...oldVal, simulationItem.id]);
          }
        }
      },
      onSettled: () => {
        setIsLoadingSimulator(false);
      },
      refetchInterval: REFETCH_INTERVAL_TIME,
      refetchOnWindowFocus: true,
    }
  );

  const { mutate: patchSimulatorMutate } = useMutation({
    mutationKey: (id) => `getSeries${id}`,
    mutationFn: ({ id, data }) => patchSimulator({ id, data }),
  });

  const handleSeriesDetailSuccess = (id, seriesData) => {
    setSeriesById((oldVal) => ({
      ...oldVal,
      [id]: seriesData,
    }));
  };

  const { data: allTagsData } = useGetAllTags({
    refetchOnWindowFocus: false,
    isSelectOptions: true,
  });

  const handleSeriesDetailError = (id) => {
    setSeriesById((oldVal) => ({
      ...oldVal,
      [id]: {
        data: oldVal?.[id]?.data || [],
        columns: oldVal?.[id]?.columns || [],
        loading: false,
        error: !!!oldVal?.[id]?.data?.length,
      },
    }));
  };

  const getSeriesDetail = (simulationData) => {
    fetchSeriesDetail(simulationData, handleSeriesDetailSuccess, handleSeriesDetailError);
  };

  const onClickCollapsed = (row) => {
    const id = row?.id;
    if (simulationIdsOpen.some((id) => id === row.id)) {
      setSimulationIdsOpen((oldVal) => [...oldVal.filter((id) => id !== row.id)]);
      return;
    }
    setSimulationIdsOpen((oldVal) => [...oldVal, row.id]);
    setSeriesById((oldVal) => ({
      ...oldVal,
      [id]: {
        ...oldVal?.[id],
        loading: true,
      },
    }));
    getSeriesDetail(row);
  };

  const onChangePage = (value) => {
    setParams({
      ...params,
      page: value,
    });
    handleScrollToTop();
  };

  const onKeyDownInput = (e) => {
    if (e.key === "Enter") {
      onSubmitSearch(e.target.value);
    }
  };

  const onSubmitSearch = (searchValues) => {
    if (params?.condition === searchValues && !isLoadingSimulator) {
      setIsLoadingSimulator(true);
      refetch();
    }
    setParams({
      page: 1,
      condition: searchValues,
    });
  };

  const handleFilterData = (name, vals) => {
    setParams((prevParams) => {
      const newParams = { ...prevParams, [name]: vals };
      newParams.page = 1;
      return newParams;
    });
  };

  const getCurrentPage = () => {
    return (params?.page || PAGINATION_DEFAULT.page) - 1;
  };

  const handleTerminationSimulation = (id, callback) =>
    patchSimulatorMutate(
      { id, data: { status: PROCESS_STATUS.TERMINATING } },
      {
        onSuccess: () => {
          setIsLoadingSimulator(true);
          refetch();
        },
        onError: () => showAlert({ status: true, message: t("list.table.terminal_fail"), type: "danger" }),
        onSettled: () => callback?.(),
      }
    );

  const isShowDashboardButton = (simData, simType, simResultType) =>
    simData.status === PROCESS_STATUS.SUCCEEDED && simData.execution_file_name === simResultType && simData.type === simType;

  const handleOpenAssignTagsModal = (id = null) => {
    if (id) {
      setSingleResourceSelected(id.toString());
    }
    setIsAssignTagModalOpen(true);
  };

  const handleCloseAssignTagsModal = () => {
    setIsAssignTagModalOpen(false);
    setSingleResourceSelected("");
  };

  useEffect(() => {
    setIsLoadingSimulator(true);
    refetch();
  }, [params]);

  useEffect(() => {
    if (resetForm) {
      setTextSearch(null);
      setResetForm(false);
    }
  }, [resetForm]);

  return (
    <Container fluid>
      <Row>
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h2 className="header-title mb-0">{t("list.title")}</h2>
                <SearchInput
                  submit={onSubmitSearch}
                  setTextSearch={setTextSearch}
                  onKeyDownInput={onKeyDownInput}
                  textSearch={textSearch}
                  placeholder={t("list.search_placeholder")}
                />
              </div>
              <Row>
                <Col md={12}>
                  <Card>
                    <div className="d-flex justify-content-end">
                      <CardHeader className="bg-white border-0">
                        <ProtectedComponent id="flight_new">
                          <CustomLink to={APP_ROUTES.FLIGHT_NEW} className="btn btn-primary text-capitalize">
                            {t("list.redirect_new")}
                          </CustomLink>
                        </ProtectedComponent>
                      </CardHeader>
                    </div>
                    <CardBody className="bg-white">
                      <TableCommon
                        loading={isLoadingSimulator}
                        columns={columns({
                          t,
                          onClickCollapsed,
                          seriesById,
                          setDataModalFileView,
                          setSeriesById,
                          handleFilterData,
                          params,
                          versionOptions,
                          excutionFileOptions,
                          userOptions,
                          allTagsData,
                          isShowDashboardButton,
                          dashboardLoading,
                          setDashboardLoading,
                          refetch,
                          handleTerminationSimulation,
                          handleOpenAssignTagsModal,
                        })}
                        data={simulations?.items || []}
                        onChangePage={onChangePage}
                        tableFixed
                        tableClassName="striped table-collapse table-fixed"
                        customClassName="overflow-auto simulation-table"
                        pageCurrent={getCurrentPage()}
                        pageCount={simulations?.pagination?.total_page}
                        totalItem={simulations?.pagination?.total}
                        pageSize={PAGINATION_DEFAULT.pageSize}
                      />
                      <ModalFlightSimulatorSeriesFilesView
                        {...dataModalFileView}
                        onClose={() => setDataModalFileView((oldVal) => ({ ...oldVal, isOpen: false }))}
                      />
                      <ModalAssignTags
                        isOpen={isAssignTagModalOpen}
                        onClose={handleCloseAssignTagsModal}
                        resourceType={RESOURCE_TYPE_FOR_TAG.FLIGHT_SIMULATOR}
                        resourcesSelected={[singleResourceSelected]}
                      />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Row>
    </Container>
  );
};

export default FlightSimulatorList;
